import { type FC, type ReactNode, useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { useGameConfig } from '../../../../config/GameConfigProvider';
import { Logger } from '../../../../logger/Logger';
import { QuickGuideService } from '../../../../services/quickGuide/QuickGuideService';
import { useQuickGuideHintVisible } from '../../../../services/quickGuide/reactBindings/hooks';
import { useActiveDialogCleaner } from '../../../../store/actions/useActiveDialogCleaner';
import { useCoreDispatch } from '../../../../store/CoreStore';
import { useGameStateSlice } from '../../../../store/slices/game/selectors';
import { useHeaderSlice } from '../../../../store/slices/header/selectors';
import { headerActions } from '../../../../store/slices/header/slice';
import { HeaderModal } from '../../../../store/slices/header/types';
import { usePromotionsV2Slice } from '../../../../store/slices/promotionsV2/selectors';
import { useIsLargerThanWide } from '../../../../styles/hooks/media';

import { QuickGuideControl } from './QuickGuideControl';

const useQuickGuideHintVisibleConnected = (): boolean => {
  const isHintVisible = useQuickGuideHintVisible();
  const showDetailsModal = usePromotionsV2Slice.getShowDetailsModal();

  return isHintVisible && !showDetailsModal;
};

const QUICK_GUIDE_TIMEOUT = 10_000;

export const QuickGuideControlConnected: FC = (): ReactNode => {
  const dispatch = useCoreDispatch();
  const openedModal = useHeaderSlice.getCoreOpenedModal();
  const isHintVisible = useQuickGuideHintVisibleConnected();
  const { quickGuideContent } = useGameConfig();
  const isWide = useIsLargerThanWide();

  const hideUI = useGameStateSlice.hideUI();
  const cleanDialogs = useActiveDialogCleaner();

  const [timer, setTimer] = useState(0);

  const handleClick = useCallback(() => {
    // UIC-64 | Hide Dialogs Functionality
    if (hideUI) {
      cleanDialogs();
      return;
    }

    if (isWide) {
      if (openedModal === HeaderModal.QuickGuide) {
        dispatch(headerActions.setOpenedModal(null));
      } else {
        Logger.logEvent('openQuickGuide');
        dispatch(headerActions.setOpenedModal(HeaderModal.QuickGuide));
        QuickGuideService.killHint();
      }
    } else {
      Logger.logEvent('openQuickGuide');
      QuickGuideService.killHint();
      dispatch(headerActions.setNavForcedExpanded(true));
      dispatch(headerActions.setNavRoute(['home', 'QUICK_GUIDE']));
    }
  }, [isWide, dispatch, openedModal, hideUI, cleanDialogs]);

  const hideButton = (): void => {
    setTimer(0);
    QuickGuideService.hideHint();
  };

  // increment hint view count once per session
  useEffect(() => {
    if (QuickGuideService.wasHintViewed) return;
    QuickGuideService.incrementHintViewCount();
  }, []);

  // handle quickguide hint visibility
  useLayoutEffect(() => {
    if (!isHintVisible) {
      hideButton();
      return;
    }

    setTimer(QUICK_GUIDE_TIMEOUT);
  }, [isHintVisible]);

  // handle quickguide hint timer
  useEffect(() => {
    if (!timer) return;

    const timeOutId = window.setTimeout(hideButton, timer);

    return (): void => {
      window.clearTimeout(timeOutId);
    };
  }, [timer]);

  if (!quickGuideContent) return null;
  if (!isWide && !isHintVisible) return null;

  return <QuickGuideControl onClick={handleClick} timer={timer} active={openedModal === HeaderModal.QuickGuide} />;
};
